import { useQuery } from '@apollo/client'
import { Autocomplete, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import {
    GetCompaniesByName,
    GetTopTierCompaniesByName,
} from './CompaniesAutocomplete.gql'
import { CompaniesAutocompleteProps } from './CompaniesAutocompleteProps'

const LIMIT = 25
/**
 * multiple + single autocompletes are separated to workaround MUI typing
 * @param CompaniesAutocompleteProps
 * @returns MUI Autocomplete with paginated options (single-select)
 */
const CompaniesAutocompleteMultiple: FC<CompaniesAutocompleteProps<true>> = ({
    error,
    helperText,
    size,
    placeholder,
    topTierOnly = false,
    label = 'Companies',
    ...props
}) => {
    const [name, setName] = useState('')
    const [debounceName, setDebouncedName] = useState(name)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedName(name)
            nextPageToFetch.current = 2
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    const { data, fetchMore, loading } = useQuery(GetCompaniesByName, {
        variables: { name: debounceName, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: topTierOnly === true || !open,
    })

    const {
        data: topTierData,
        fetchMore: fetchMoreTopTier,
        loading: topTierLoading,
    } = useQuery(GetTopTierCompaniesByName, {
        variables: { name: debounceName, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: topTierOnly === false || !open,
    })

    const loadMoreData = useCallback(() => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                name: debounceName,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.companiesByName?.data ||
                    !fetchMoreResult?.companiesByName?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedCompaniesData = Array.from(
                    previousData.companiesByName.data
                )
                const pageLimit =
                    previousData.companiesByName.pageInfo.pageTotal
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.companiesByName.data.length;
                    i++
                ) {
                    updatedCompaniesData[(page - 1) * limit + i] =
                        fetchMoreResult.companiesByName.data[i]
                }
                const newCache = {
                    ...previousData,
                    companiesByName: {
                        ...fetchMoreResult.companiesByName,
                        data: updatedCompaniesData,
                    },
                }
                if (
                    fetchMoreResult?.companiesByName?.pageInfo.itemTotal &&
                    fetchMoreResult.companiesByName.pageInfo.itemTotal ===
                        updatedCompaniesData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMore, debounceName])

    const loadMoreTopTier = useCallback(() => {
        fetchMoreTopTier({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                name: debounceName,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                const updatedCompaniesData = Array.from(
                    previousData?.topTierCompanies?.data || []
                )
                const pageLimit =
                    previousData?.topTierCompanies?.pageInfo.pageTotal || 1
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                if (!fetchMoreResult?.topTierCompanies?.data)
                    return previousData
                for (
                    let i = 0;
                    i < fetchMoreResult?.topTierCompanies?.data?.length;
                    i++
                ) {
                    updatedCompaniesData[(page - 1) * limit + i] =
                        fetchMoreResult?.topTierCompanies.data[i]
                }
                const newCache = {
                    ...previousData,
                    topTierCompanies: {
                        ...fetchMoreResult.topTierCompanies,
                        data: updatedCompaniesData,
                    },
                }
                if (
                    fetchMoreResult?.topTierCompanies?.pageInfo.itemTotal &&
                    fetchMoreResult.topTierCompanies.pageInfo.itemTotal ===
                        updatedCompaniesData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMoreTopTier, debounceName])

    const observerCallback = useCallback(
        (entries) => {
            const loadMore = topTierOnly ? loadMoreTopTier : loadMoreData
            if (entries[0].isIntersecting) {
                loadMore()
            }
        },
        [loadMoreData, loadMoreTopTier, topTierOnly]
    )

    const observedElementRef = useRef<HTMLLIElement | null>(null)
    const nextPageToFetch = useRef<number>(2)
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver(observerCallback)
    )

    useEffect(() => {
        observer.current = new IntersectionObserver(observerCallback)
        // update which element we are observing as data changes
        if (observedElementRef.current) {
            observer.current?.observe(observedElementRef.current)
        }

        return () => {
            if (observer.current) observer.current.disconnect()
        }
        // open is a dependency because we lose the observedElementRef when the dropdown closes and need to re-observe it once we open again
    }, [observerCallback, open, data?.companiesByName.data])

    return (
        <Autocomplete
            fullWidth
            multiple
            getOptionLabel={(option) => option.value}
            filterOptions={(x) => x}
            disablePortal
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
                if (reason !== 'selectOption' && reason !== 'removeOption') {
                    setOpen(false)
                }
            }}
            loading={topTierOnly ? topTierLoading : loading}
            open={open}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, state, ownerState) => {
                const index = state.index
                const totalOptions = ownerState.options.length
                const isObserver = index === totalOptions - 1
                return (
                    <li
                        {...props}
                        key={option.id}
                        ref={isObserver ? observedElementRef : null}
                    >
                        {option.value}
                    </li>
                )
            }}
            onInputChange={(e, value) => {
                setName(value)
            }}
            options={
                topTierOnly
                    ? topTierData?.topTierCompanies?.data || []
                    : data?.companiesByName.data || []
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )}
            {...props}
        />
    )
}

export default CompaniesAutocompleteMultiple
